/**
 * URL builders for drilldown links from dashboard tiles into existing
 * list pages. Each builder returns a `{ to, search }` pair suitable
 * for `<Link to={to} search={search}>`.
 *
 * Drilldown URLs always carry:
 *   - `from=dashboard` — boolean signal the destination page uses to
 *     show a "← Dashboard" banner and suppress preference writes.
 *   - `dashFrom`, `dashTo`, `dashPreset` — the dashboard's date filter
 *     state at the moment the link was clicked. The banner reads these
 *     to reconstruct the dashboard URL for the back link.
 *   - `dashLabel` — human-readable context for the banner (e.g. "Cathy",
 *     "Referral", "Completed tasks (Last 30 days)"). The banner uses
 *     this to render "Filtered by <label>" rather than a generic
 *     "Filtered from dashboard" string. Pure display — server doesn't
 *     read it.
 *
 * For period-bounded metrics (completed_tasks, completion_rate,
 * referrals_count, the closed half of tasks_by_label/tasks_by_assignee),
 * the dashboard's `dateFrom`/`dateTo` are also propagated as the
 * destination page's `dateFrom`/`dateTo` so the filtered list scopes
 * to the same window the user was looking at.
 */

import type { OpenTaskAgeBucket, TasksByAssigneeItem, TasksByLabelItem } from '@vagusx/clients';
import type { TasksSearch } from '@/pages/tasks/search-schema';
import type { DocumentsSearch } from '@/pages/documents/search-schema';

/**
 * Every URL key a drilldown may inject. The DrilldownBanner's
 * "× Clear filter" button strips exactly these keys (so the user
 * stays on the destination page but with their unfiltered view).
 *
 * Keep in sync with the builders below — when a new drilldown adds a
 * URL param (e.g. `dashWindow`), add it here too. Splitting this list
 * across files (banner had its own copy) was the source of past bugs
 * where Clear-filter forgot to strip a newly-added key.
 */
export const DRILLDOWN_PARAM_KEYS: ReadonlySet<string> = new Set([
  // Carry-through (signals + back-link reconstruction)
  'from',
  'dashFrom',
  'dashTo',
  'dashPreset',
  'dashLabel',
  // Filter params drilldowns inject into destination URLs
  'assignedTo',
  'assigneeType',
  'status',
  'labels',
  'dateFrom',
  'dateTo',
  // Document-extractions drilldown (`classification=referral`)
  'classification',
]);

/**
 * A link descriptor consumed by the tile components. The generic
 * parameter `TSearch` is the destination page's search-schema type
 * (`TasksSearch`, `DocumentsSearch`, etc.); each builder
 * narrows it so a typo'd or unknown URL key is a compile error at
 * the source.
 *
 * Tile components (StatTile, ListTile, TableTile) read drilldowns
 * polymorphically — they pass `search` straight through to TanStack
 * Router's `<Link>`, which accepts a wide search shape. So they
 * declare their prop type as `DrilldownLink` (no generic) which is
 * an alias for the widest acceptable shape.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any -- variance erased for downstream consumers
export interface DrilldownLink<TSearch = Record<string, any>> {
  /** Destination route, must match a TanStack Router file route. */
  to: string;
  /** Search params (URL query) — values are stringified by the router. */
  search: TSearch;
  /** Optional title for accessibility / hover. */
  title?: string;
}

interface DashboardContext {
  /** Dashboard's current `dateFrom` URL param (ISO). */
  dashFrom: string;
  /** Dashboard's current `dateTo` URL param (ISO). */
  dashTo: string;
  /** Dashboard's current preset label. */
  dashPreset: string;
}

/**
 * Common params that propagate the dashboard context onto the
 * drilldown URL. Caller spreads this into their own `search` and
 * passes its own human-readable `label` to surface in the banner.
 *
 * Shape matches the bookkeeping fields on both `tasks` and
 * `documents` search schemas — those destinations both
 * accept the same drilldown bookkeeping keys, so a single carry-through
 * helper keeps both happy.
 */
export interface DrilldownCarryThrough {
  from: string;
  dashFrom: string;
  dashTo: string;
  dashPreset: string;
  dashLabel: string;
}

function dashboardCarryThrough(ctx: DashboardContext, label: string): DrilldownCarryThrough {
  return {
    from: 'dashboard',
    dashFrom: ctx.dashFrom,
    dashTo: ctx.dashTo,
    dashPreset: ctx.dashPreset,
    dashLabel: label,
  };
}

// The Tasks inbox parses status values via `STATUS_TO_API`, which only
// knows the bucket names `open` (= ['open']) and `done` (= ['completed']).
// Sending the underlying enum values directly silently drops any
// value that isn't a bucket name. Use the bucket name to keep drilldown
// counts equal to the dashboard tile counts.
const OPEN_STATUS_PARAM = 'open';

/**
 * The Tasks inbox API regex-validates `dateFrom` / `dateTo` as
 * `YYYY-MM-DD`. The dashboard URL stores its window as full ISO
 * datetimes (`2026-04-30T07:00:00.000Z`) for second-level precision;
 * carrying that ISO straight into a tasks drilldown would 422 the
 * API call. Truncate to date-only when the destination is `/tasks`.
 *
 * `/documents` accepts a looser string today — but using
 * the same date-only format keeps the URL consistent and avoids
 * mixed formats across destinations.
 */
function toDateOnly(iso: string | undefined): string | undefined {
  if (!iso) return iso;
  return iso.slice(0, 10);
}

// =============================================================================
// Per-tile drilldowns
// =============================================================================

export const drilldowns = {
  /** Open-tasks tile → /tasks filtered to open statuses. */
  openTasks(ctx: DashboardContext): DrilldownLink<TasksSearch> {
    return {
      to: '/tasks',
      search: {
        ...dashboardCarryThrough(ctx, 'Open tasks'),
        status: OPEN_STATUS_PARAM,
      } satisfies TasksSearch,
      title: 'View open tasks',
    };
  },

  /**
   * Completed-in-window tile → /tasks filtered to completed status,
   * scoped to the dashboard's date window.
   */
  completedTasks(ctx: DashboardContext): DrilldownLink<TasksSearch> {
    return {
      to: '/tasks',
      search: {
        ...dashboardCarryThrough(ctx, `Completed (${ctx.dashPreset})`),
        status: 'done',
        dateFrom: toDateOnly(ctx.dashFrom),
        dateTo: toDateOnly(ctx.dashTo),
      } satisfies TasksSearch,
      title: 'View completed tasks',
    };
  },

  /**
   * Completion-rate tile → /tasks filtered to completed in window.
   * (We drill into the numerator, which is the actionable side — "what
   * did we ship" — rather than the denominator's mixed bag.)
   */
  completionRate(ctx: DashboardContext): DrilldownLink<TasksSearch> {
    return {
      to: '/tasks',
      search: {
        ...dashboardCarryThrough(ctx, `Completed (${ctx.dashPreset})`),
        status: 'done',
        dateFrom: toDateOnly(ctx.dashFrom),
        dateTo: toDateOnly(ctx.dashTo),
      } satisfies TasksSearch,
      title: 'View completed tasks',
    };
  },

  /**
   * Referrals tile → `/documents` filtered to `classification=referral`
   * within the window. Tile name and drilldown destination must agree —
   * if the tile is "Referrals", the page must show only referrals.
   */
  referralsCount(ctx: DashboardContext): DrilldownLink<DocumentsSearch> {
    return {
      to: '/documents',
      search: {
        ...dashboardCarryThrough(ctx, `Referrals (${ctx.dashPreset})`),
        classification: 'referral',
        dateFrom: toDateOnly(ctx.dashFrom),
        dateTo: toDateOnly(ctx.dashTo),
      } satisfies DocumentsSearch,
      title: 'View referrals',
    };
  },

  /**
   * Active-users tile → /setup/users. The users page has no
   * `validateSearch` schema; type the destination explicitly against
   * the carry-through bookkeeping shape so unrelated keys can't be
   * accidentally added.
   */
  activeUsers(ctx: DashboardContext): DrilldownLink<DrilldownCarryThrough> {
    return {
      to: '/setup/users',
      search: dashboardCarryThrough(ctx, 'Active users'),
      title: 'View users',
    };
  },

  // ---------------------------------------------------------------------------
  // Per-row drilldowns (list/table tiles)
  // ---------------------------------------------------------------------------

  /**
   * Per-assignee row drilldown. Carries `assigneeType` so the destination
   * page can filter by `(assigned_to_id, assigned_to_type)` together —
   * critical for team rows, since a UUID could in principle collide
   * across the two namespaces (and the filter is more honest when the
   * intent — "tasks owned by this team" — is explicit).
   *
   * Three cells per row:
   *   - row label → open queue for this assignee (matches operator
   *     intent: clicking "Ada" opens what's still on her plate)
   *   - open cell → same as row label
   *   - completed cell → window-bounded done queue
   */
  tasksByAssigneeRow(item: TasksByAssigneeItem, ctx: DashboardContext): DrilldownLink<TasksSearch> {
    return {
      to: '/tasks',
      search: {
        ...dashboardCarryThrough(ctx, item.assigneeName),
        status: OPEN_STATUS_PARAM,
        assignedTo: item.assigneeId,
        assigneeType: item.assigneeType,
      } satisfies TasksSearch,
      title: `Open tasks for ${item.assigneeName}`,
    };
  },

  tasksByAssigneeOpen(
    item: TasksByAssigneeItem,
    ctx: DashboardContext
  ): DrilldownLink<TasksSearch> {
    return {
      to: '/tasks',
      search: {
        ...dashboardCarryThrough(ctx, `${item.assigneeName} · open`),
        status: OPEN_STATUS_PARAM,
        assignedTo: item.assigneeId,
        assigneeType: item.assigneeType,
      } satisfies TasksSearch,
      title: `Open tasks for ${item.assigneeName}`,
    };
  },

  tasksByAssigneeCompleted(
    item: TasksByAssigneeItem,
    ctx: DashboardContext
  ): DrilldownLink<TasksSearch> {
    return {
      to: '/tasks',
      search: {
        ...dashboardCarryThrough(ctx, `${item.assigneeName} · completed`),
        status: 'done',
        assignedTo: item.assigneeId,
        assigneeType: item.assigneeType,
        dateFrom: toDateOnly(ctx.dashFrom),
        dateTo: toDateOnly(ctx.dashTo),
      } satisfies TasksSearch,
      title: `Completed tasks for ${item.assigneeName}`,
    };
  },

  /**
   * Per-label row drilldowns. Three cells per row:
   *   - row label → all tasks with this label (status-agnostic)
   *   - open cell → tasks with this label, open statuses
   *   - closed cell → tasks with this label, completed (not window-bounded
   *     because the metric itself isn't — see DashboardService.tasksByLabel)
   */
  tasksByLabelRow(item: TasksByLabelItem, ctx: DashboardContext): DrilldownLink<TasksSearch> {
    return {
      to: '/tasks',
      search: {
        ...dashboardCarryThrough(ctx, item.labelName),
        labels: item.labelSlug,
      } satisfies TasksSearch,
      title: `Tasks labeled "${item.labelName}"`,
    };
  },

  tasksByLabelOpen(item: TasksByLabelItem, ctx: DashboardContext): DrilldownLink<TasksSearch> {
    return {
      to: '/tasks',
      search: {
        ...dashboardCarryThrough(ctx, `${item.labelName} · open`),
        status: OPEN_STATUS_PARAM,
        labels: item.labelSlug,
      } satisfies TasksSearch,
      title: `Open tasks labeled "${item.labelName}"`,
    };
  },

  tasksByLabelClosed(item: TasksByLabelItem, ctx: DashboardContext): DrilldownLink<TasksSearch> {
    return {
      to: '/tasks',
      search: {
        ...dashboardCarryThrough(ctx, `${item.labelName} · completed`),
        status: 'done',
        labels: item.labelSlug,
      } satisfies TasksSearch,
      title: `Closed tasks labeled "${item.labelName}"`,
    };
  },

  /**
   * Open-task-age bucket drilldown. Translates "tasks created N to M
   * days ago" into the inbox's `dateFrom`/`dateTo` filter.
   *   - dateFrom = today − maxDays  (older end of the bucket)
   *   - dateTo   = today − minDays  (newer end)
   * For the open-ended tail (`maxDays === null`), `dateFrom` is omitted
   * so the filter selects everything older than `minDays`.
   *
   * Date format is YYYY-MM-DD per the inbox schema regex.
   */
  openTasksByAge(bucket: OpenTaskAgeBucket, ctx: DashboardContext): DrilldownLink<TasksSearch> {
    const today = new Date();
    const dayMs = 24 * 60 * 60 * 1000;
    const fmt = (d: Date): string => d.toISOString().slice(0, 10);
    const dateTo = fmt(new Date(today.getTime() - bucket.minDays * dayMs));
    const dateFrom =
      bucket.maxDays === null ? undefined : fmt(new Date(today.getTime() - bucket.maxDays * dayMs));

    return {
      to: '/tasks',
      search: {
        ...dashboardCarryThrough(ctx, `Open · ${bucket.label}`),
        status: OPEN_STATUS_PARAM,
        dateFrom,
        dateTo,
      } satisfies TasksSearch,
      title: `Open tasks aged ${bucket.label}`,
    };
  },

  /**
   * Aging-heatmap cell drilldown. Combines the per-bucket date logic
   * from `openTasksByAge` with an assignee filter from the row, so a
   * click on "Cathy × 7-14 days" lands on the inbox filtered to her
   * stale work.
   */
  agingHeatmapCell(
    row: { assigneeId: string; assigneeType: 'user' | 'team'; assigneeName: string },
    bucket: { label: string; minDays: number; maxDays: number | null },
    ctx: DashboardContext
  ): DrilldownLink<TasksSearch> {
    const today = new Date();
    const dayMs = 24 * 60 * 60 * 1000;
    const fmt = (d: Date): string => d.toISOString().slice(0, 10);
    const dateTo = fmt(new Date(today.getTime() - bucket.minDays * dayMs));
    const dateFrom =
      bucket.maxDays === null ? undefined : fmt(new Date(today.getTime() - bucket.maxDays * dayMs));

    return {
      to: '/tasks',
      search: {
        ...dashboardCarryThrough(ctx, `${row.assigneeName} · ${bucket.label}`),
        status: OPEN_STATUS_PARAM,
        assignedTo: row.assigneeId,
        assigneeType: row.assigneeType,
        dateFrom,
        dateTo,
      } satisfies TasksSearch,
      title: `${row.assigneeName}'s open tasks aged ${bucket.label}`,
    };
  },
};

export type DrilldownBuilders = typeof drilldowns;
